import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * ErrorBoundary - Catches React errors and displays user-friendly fallback UI
 *
 * Prevents the entire app from crashing when a component throws an error.
 * Shows a helpful error message and allows users to recover by returning home.
 *
 * Usage:
 * <ErrorBoundary fallbackMessage="Custom error message">
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Features:
 * - User-friendly error display
 * - "Return to Home" recovery button
 * - Error logging to console (future: send to Sentry/LogRocket)
 * - Development mode: shows full error stack trace
 * - Production mode: shows only user-friendly message
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (future: send to error tracking service)
    console.error('ErrorBoundary caught error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Future: Send to error tracking service
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/'; // Navigate home to reset app state
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 3,
            textAlign: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />

          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Oops! Something went wrong
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600 }}>
            {this.props.fallbackMessage ||
              'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
          </Typography>

          {/* Show error details in development mode */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: '#fff',
              border: '1px solid #ddd',
              borderRadius: 1,
              maxWidth: 800,
              width: '100%',
              textAlign: 'left',
              overflow: 'auto',
              maxHeight: 400
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'error.main' }}>
                Error Details (Development Mode):
              </Typography>
              <Typography variant="caption" component="pre" sx={{
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={this.handleReset}
            sx={{ mt: 3 }}
          >
            Return to Home
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
